import { Mesh, Object3D } from "three";
import { ISpawnable } from "../../spawner/ISpawnable";
import { Event } from "event-system";
import { GloryHole } from "../GloryHole";
import { GameResourcesPackage } from "../../../../resources/GameResourcesPackage";
import { clone } from "three/examples/jsm/utils/SkeletonUtils.js";
import { EatingMole } from "./EatingMole";
import { Character } from "../../character/Character";
import { AudioController } from "../../../AudioController";
import { IProjectileContainer } from "../IProjectileContainer";
import { GameElement } from "../../../../models/field/GameElement";
import { Vegetable } from "../Vegetable";
import { BaseMole } from "./BaseMole";
import { AttackingMole } from "./AttackingMole";
import { AggressiveEaterMole } from "./AggressiveEaterMole";
import { BatchedRenderer } from "three.quarks";
import { GameController } from "../../../../controllers/GameController";
import { HoleClodsParticle } from "../particles/HoleClodsParticle";
import { CancellationToken } from "../../../../../../../composition/CancellationToken";
import { TutorialMole } from "./TutorialMole";
import { FontResourcesPackage } from "../../../../../../common/resources/FontResourcesPackage";
import { LocalizationModel } from "../../../../../../common/models/LocalizationModel";

export class MoleHole extends Object3D implements ISpawnable {
    public readonly onCanBeDestroyed: Event<ISpawnable>;
    public readonly cancellation: CancellationToken;
    private hole: GloryHole;
    private particle: HoleClodsParticle;
    private _mole: BaseMole;

    public get mole(): BaseMole {
        return this._mole;
    }

    public constructor(
        private readonly gameResources: GameResourcesPackage,
        private readonly fontResources: FontResourcesPackage,
        private readonly localization: LocalizationModel,
        private readonly moleType: GameElement,
        private readonly projectileContainer: IProjectileContainer,
        private readonly particlesRenderer: BatchedRenderer,
        private readonly character: Character,
        private readonly vegetable: Vegetable,
        private readonly audioController: AudioController,
        private readonly gameController: GameController,
        public readonly fieldIndex: number
    ) {
        super();
        this.onCanBeDestroyed = new Event();
        this.cancellation = new CancellationToken();
        this.cancellation.onCanceled.add(this.cancel, this);
        this.addEventListener("added", this.onAddedToStage);
        this.hole = new GloryHole(clone(gameResources.assets_game_Hole_glb.scene.children[0]) as Mesh);
        this.add(this.hole);
    }

    private async onAddedToStage(): Promise<void> {
        await this.hole.open(500);

        if (this.cancellation.isCancellationRequested) {
            return;
        }

        if (!this.vegetable?.alive) {
            this._mole = new AttackingMole(this.gameResources, this.projectileContainer, this.particlesRenderer, this.character, this.audioController, this.gameController, this.fieldIndex);
        } else {
            switch (this.moleType) {
                case GameElement.AttackingMole: {
                    this._mole = new AttackingMole(this.gameResources, this.projectileContainer, this.particlesRenderer, this.character, this.audioController, this.gameController, this.fieldIndex);
                    break;
                }
                case GameElement.EatingMole: {
                    this._mole = new EatingMole(this.gameResources, this.projectileContainer, this.particlesRenderer, this.character, this.vegetable, this.audioController, this.gameController, this.fieldIndex);
                    break;
                }
                case GameElement.AggressiveEaterMole: {
                    this._mole = new AggressiveEaterMole(this.gameResources, this.projectileContainer, this.particlesRenderer, this.character, this.vegetable, this.audioController, this.gameController, this.fieldIndex)
                    break;
                }
                case GameElement.TutorialMole: {
                    this._mole = new TutorialMole(this.gameResources, this.fontResources, this.localization, this.projectileContainer, this.particlesRenderer, this.character, this.vegetable, this.audioController, this.gameController, this.fieldIndex);
                    break;
                }
            }
        }
        this._mole.onCanBeDestroyed.once(this.onMoleCanBeDestroyed, this);

        this.particle = new HoleClodsParticle(this.gameResources, this.particlesRenderer);
        this.particle.position.set(0, 0.5, -0.7);
        this.add(this.particle, this._mole);
        this.particle.play();
    }

    private async onMoleCanBeDestroyed(): Promise<void> {
        this._mole.destroy();
        await this.hole.close(500);
        this.destroy();
    }

    private async cancel(): Promise<void> {
        await this.hole.close(500);
        this.destroy();
    }

    public destroy(): void {
        this.cancellation.onCanceled.remove(this.cancel, this);
        this.removeEventListener("added", this.onAddedToStage);
        this._mole?.destroy();
        this.hole.destroy();
        this.onCanBeDestroyed.emit(this);
    }
}
